import { Box, Button, Group, List, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconExclamationCircleFilled } from '@tabler/icons-react';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useHistory } from 'react-router';
import { isNonNullish } from 'remeda';
import type { IgnoredReason } from 'types/reminder-status';

import type { Reminder } from '../api';

interface StatusCardProps {
  reminder: Reminder;
}

export const StatusCard = ({ reminder }: StatusCardProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const history = useHistory();

  const hasFailed = reminder.status === 'failed';
  const isOriginalReminder = reminder.resentVia;
  const hasBeenIgnored = reminder.status === 'ignored';
  const hasBeenResent = isNonNullish(reminder.original);
  const hasBeenAutomaticallyResent = reminder.automaticResent;

  const getIgnoredReason = (reason: IgnoredReason) => {
    if (reason === 'paid') return t(i18nKeys.FAILED_REMINDERS.STATUS.IGNORE_PAID);
    if (reason === 'manually_ignored') return t(i18nKeys.FAILED_REMINDERS.STATUS.MANUALLY_IGNORE);
    if (reason === 'automatically_ignored')
      return t(i18nKeys.FAILED_REMINDERS.STATUS.AUTOMATICALLY_IGNORE);
    return t(i18nKeys.FAILED_REMINDERS.STATUS.IGNORED);
  };

  const boxProps = { p: 'lg', radius: 'md', style: { borderRadius: '8px' } };

  if (hasBeenAutomaticallyResent) {
    return (
      <Box bg="blue.0" {...boxProps}>
        <Group maw={600} align="flex-start">
          <IconExclamationCircleFilled size={24} color={theme.colors.blue[5]} />
          <Stack maw={400}>
            <Text>{t(i18nKeys.FAILED_REMINDERS.STATUS.AUTOMATICALLY_RESENT)}</Text>
            <Button onClick={() => history.push(`/reminders/${reminder.resentVia}`)}>
              <Text>{t(i18nKeys.FAILED_REMINDERS.STATUS.BUTTON_SEE_REMINDER)}</Text>
            </Button>
          </Stack>
        </Group>
      </Box>
    );
  }

  if (hasBeenResent && hasFailed) {
    return (
      <Box bg="orange.0" {...boxProps}>
        <Group maw={600} align="flex-start">
          <IconExclamationCircleFilled size={24} color={theme.colors.orange[5]} />
          <Stack maw={400}>
            <Text>{t(i18nKeys.FAILED_REMINDERS.STATUS.RESENT_FAILED)}</Text>
            <Button onClick={() => history.push(`/reminders/${reminder.original}`)}>
              <Text>{t(i18nKeys.FAILED_REMINDERS.STATUS.BUTTON_SEE_ORIGINAL)}</Text>
            </Button>
          </Stack>
        </Group>
      </Box>
    );
  }

  if (hasBeenResent) {
    return (
      <Box bg="blue.0" {...boxProps}>
        <Group maw={600} align="flex-start">
          <IconExclamationCircleFilled size={24} color={theme.colors.blue[5]} />
          <Stack maw={400}>
            <Text>{t(i18nKeys.FAILED_REMINDERS.STATUS.RESENT)}</Text>
            <Button onClick={() => history.push(`/reminders/${reminder.original}`)}>
              <Text>{t(i18nKeys.FAILED_REMINDERS.STATUS.BUTTON_SEE_ORIGINAL)}</Text>
            </Button>
          </Stack>
        </Group>
      </Box>
    );
  }

  if (hasBeenIgnored) {
    return (
      <Box bg="orange.0" {...boxProps}>
        <Group maw={600} align="flex-start">
          <IconExclamationCircleFilled size={24} color={theme.colors.orange[5]} />
          <Stack maw={400} gap={0}>
            <Text>
              {reminder.ignoredReason
                ? getIgnoredReason(reminder.ignoredReason)
                : t(i18nKeys.FAILED_REMINDERS.STATUS.IGNORED)}
            </Text>
            <List>
              {reminder?.emails
                .filter(email => email.failed)
                .map(email => (
                  <List.Item key={email.email}>
                    <Text>{email.email}</Text>
                  </List.Item>
                ))}
            </List>
          </Stack>
        </Group>
      </Box>
    );
  }

  if (isOriginalReminder) {
    return (
      <Box bg="blue.0" {...boxProps}>
        <Group miw={300} align="flex-start">
          <IconExclamationCircleFilled size={24} color={theme.colors.blue[5]} />
          <Stack maw={400}>
            <Text>
              {t(
                reminder.emails.some(email => email.failed)
                  ? i18nKeys.FAILED_REMINDERS.STATUS.ORIGINAL_REMINDER
                  : i18nKeys.FAILED_REMINDERS.STATUS.MANUALLY_RESENT,
              )}
            </Text>
            <List>
              {reminder?.emails
                .filter(email => email.failed)
                .map(email => (
                  <List.Item key={email.email}>
                    <Text>{email.email}</Text>
                  </List.Item>
                ))}
            </List>
            {reminder.resentVia && (
              <Button onClick={() => history.push(`/reminders/${reminder.resentVia}`)}>
                <Text>{t(i18nKeys.FAILED_REMINDERS.STATUS.BUTTON_SEE_REMINDER)}</Text>
              </Button>
            )}
          </Stack>
        </Group>
      </Box>
    );
  }

  if (hasFailed) {
    return (
      <Box bg="orange.0" {...boxProps}>
        <Group maw={600} align="flex-start">
          <IconExclamationCircleFilled size={24} color={theme.colors.orange[5]} />
          <Stack maw={400} gap={0}>
            <Text>{t(i18nKeys.FAILED_REMINDERS.STATUS.FAILED)}</Text>
            <List>
              {reminder?.emails
                .filter(email => email.failed)
                .map(email => (
                  <List.Item key={email.email}>
                    <Text>{email.email}</Text>
                  </List.Item>
                ))}
            </List>
          </Stack>
        </Group>
      </Box>
    );
  }

  return null;
};
